import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const BusinessProductContext = createContext();

export const useBusinessProduct = () => {
  const context = useContext(BusinessProductContext);
  if (!context) {
    throw new Error('useBusinessProduct must be used within a BusinessProductProvider');
  }
  return context;
};

export const BusinessProductProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  // API base URL for business product endpoints
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';
  
  // Determine the correct base URL based on login type
  const getProductBaseUrl = useCallback(() => {
    if (loginType === 'master') {
      return `${API_BASE_URL}/master/business-products/api/v1/business-products`;
    } else if (loginType === 'admin') {
      return `${API_BASE_URL}/admin/business-products/api/v1/business-products`;
    } else {
      throw new Error('Invalid login type for business products access');
    }
  }, [API_BASE_URL, loginType]);

  // Helper function to handle API responses
  const handleApiResponse = async (response) => {
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || data.message || 'Request failed');
    }
    
    return data;
  };

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Validate user access
  const validateAccess = useCallback(() => {
    if (loginType !== 'admin' && loginType !== 'master') {
      throw new Error('Only admin and master users can access business products');
    }
  }, [loginType]);

  // Get products by registry number with pagination and filters
  const getProductsByRegistryNumber = useCallback(async (registryNumber, options = {}) => {
    validateAccess();
    
    if (!registryNumber) {
      throw new Error('Registry number is required');
    }

    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();
      
      if (options.limit) queryParams.append('limit', options.limit);
      if (options.offset) queryParams.append('offset', options.offset);
      if (options.productStatus) queryParams.append('product_status', options.productStatus);
      if (options.productVisible !== undefined) queryParams.append('product_visible', options.productVisible);
      if (options.minPrice) queryParams.append('min_price', options.minPrice);
      if (options.maxPrice) queryParams.append('max_price', options.maxPrice);
      if (options.search) queryParams.append('search', options.search);

      const baseUrl = getProductBaseUrl();
      const url = `${baseUrl}/registry/${encodeURIComponent(registryNumber)}`;
      const fullUrl = queryParams.toString() ? `${url}?${queryParams.toString()}` : url;
      
      const response = await makeAuthenticatedRequest(fullUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch products';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getProductBaseUrl]);

  // Get product statistics by registry number
  const getProductStats = useCallback(async (registryNumber) => {
    validateAccess();
    
    if (!registryNumber) {
      throw new Error('Registry number is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getProductBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/registry/${encodeURIComponent(registryNumber)}/stats`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get product statistics';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getProductBaseUrl]);

  // Get product statuses by registry number
  const getProductStatuses = useCallback(async (registryNumber) => {
    validateAccess();
    
    if (!registryNumber) {
      throw new Error('Registry number is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getProductBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/registry/${encodeURIComponent(registryNumber)}/statuses`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get product statuses';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getProductBaseUrl]);

  // Get product by database ID
  const getProductById = useCallback(async (productId) => {
    validateAccess();
    
    if (!productId) {
      throw new Error('Product ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getProductBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/${productId}`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get product by ID';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getProductBaseUrl]);

  // Get product by product_id field
  const getProductByProductId = useCallback(async (productId) => {
    validateAccess();
    
    if (!productId) {
      throw new Error('Product ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getProductBaseUrl();
      const response = await makeAuthenticatedRequest(`${baseUrl}/product/${encodeURIComponent(productId)}`);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get product by product ID';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getProductBaseUrl]);

  // Get products filtered by status
  const getProductsByStatus = useCallback(async (registryNumber, status, options = {}) => {
    validateAccess();

    const filterOptions = {
      ...options,
      productStatus: status,
    };

    return await getProductsByRegistryNumber(registryNumber, filterOptions);
  }, [getProductsByRegistryNumber, validateAccess]);

  // Get products filtered by visibility
  const getProductsByVisibility = useCallback(async (registryNumber, isVisible, options = {}) => {
    validateAccess();

    const filterOptions = {
      ...options,
      productVisible: isVisible,
    };

    return await getProductsByRegistryNumber(registryNumber, filterOptions);
  }, [getProductsByRegistryNumber, validateAccess]);

  // Get products by price range
  const getProductsByPriceRange = useCallback(async (registryNumber, minPrice, maxPrice, options = {}) => {
    validateAccess();

    const filterOptions = {
      ...options,
      minPrice: minPrice,
      maxPrice: maxPrice,
    };

    return await getProductsByRegistryNumber(registryNumber, filterOptions);
  }, [getProductsByRegistryNumber, validateAccess]);

  // Search products
  const searchProducts = useCallback(async (registryNumber, searchTerm, options = {}) => {
    validateAccess();

    const filterOptions = {
      ...options,
      search: searchTerm,
    };

    return await getProductsByRegistryNumber(registryNumber, filterOptions);
  }, [getProductsByRegistryNumber, validateAccess]);

  // Get visible products only
  const getVisibleProducts = useCallback(async (registryNumber, options = {}) => {
    validateAccess();
    return await getProductsByVisibility(registryNumber, true, options);
  }, [getProductsByVisibility, validateAccess]);

  // Get in-stock products only
  const getInStockProducts = useCallback(async (registryNumber, options = {}) => {
    validateAccess();
    return await getProductsByStatus(registryNumber, 'in stock', options);
  }, [getProductsByStatus, validateAccess]);

  // Get out-of-stock products only
  const getOutOfStockProducts = useCallback(async (registryNumber, options = {}) => {
    validateAccess();
    return await getProductsByStatus(registryNumber, 'out of stock', options);
  }, [getProductsByStatus, validateAccess]);

  // Export products data (prepare data for CSV/Excel export)
  const exportProductsData = useCallback(async (registryNumber, filters = {}) => {
    validateAccess();

    // Get all products without pagination for export
    const exportOptions = {
      ...filters,
      limit: 10000, // Large limit for export
      offset: 0,
    };

    const result = await getProductsByRegistryNumber(registryNumber, exportOptions);
    
    // Transform data for export
    const exportData = result.products.map(product => ({
      'Product ID': product.product_id,
      'Product Name': product.product_item,
      'Description': product.product_description,
      'Price': `$${product.product_price.toFixed(2)}`,
      'Status': product.product_status,
      'Visible': product.product_visible ? 'Yes' : 'No',
      'Database ID': product.id,
    }));

    return {
      data: exportData,
      totalCount: result.total_count,
      registryNumber: registryNumber,
      filename: `products_${registryNumber}_${new Date().toISOString().split('T')[0]}.csv`
    };
  }, [getProductsByRegistryNumber, validateAccess]);

  // Health check for business products service
  const checkHealth = useCallback(async () => {
    validateAccess();

    try {
      const healthUrl = loginType === 'master' 
        ? `${API_BASE_URL}/master/business-products/health`
        : `${API_BASE_URL}/admin/business-products/health`;
      
      const response = await makeAuthenticatedRequest(healthUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Health check failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [makeAuthenticatedRequest, validateAccess, API_BASE_URL, loginType]);

  // Validation helpers
  const validateProductFilters = useCallback((filters) => {
    const errors = [];

    if (filters.limit && (filters.limit < 1 || filters.limit > 1000)) {
      errors.push({ field: 'limit', message: 'Limit must be between 1 and 1000' });
    }

    if (filters.offset && filters.offset < 0) {
      errors.push({ field: 'offset', message: 'Offset must be non-negative' });
    }

    if (filters.minPrice && filters.minPrice < 0) {
      errors.push({ field: 'minPrice', message: 'Minimum price must be non-negative' });
    }

    if (filters.maxPrice && filters.maxPrice < 0) {
      errors.push({ field: 'maxPrice', message: 'Maximum price must be non-negative' });
    }

    if (filters.minPrice && filters.maxPrice && filters.minPrice > filters.maxPrice) {
      errors.push({ field: 'priceRange', message: 'Minimum price must be less than maximum price' });
    }

    if (filters.productStatus && !['in stock', 'out of stock', 'discontinued', 'coming soon'].includes(filters.productStatus.toLowerCase())) {
      errors.push({ field: 'productStatus', message: 'Invalid product status' });
    }

    if (filters.search && filters.search.length < 2) {
      errors.push({ field: 'search', message: 'Search term must be at least 2 characters' });
    }

    return errors;
  }, []);

  // Format product data for display
  const formatProductData = useCallback((product) => {
    return {
      ...product,
      formattedPrice: `$${product.product_price.toFixed(2)}`,
      statusColor: product.product_status === 'in stock' ? 'green' :
                   product.product_status === 'out of stock' ? 'red' :
                   product.product_status === 'discontinued' ? 'gray' :
                   product.product_status === 'coming soon' ? 'blue' : 'orange',
      visibilityText: product.product_visible ? 'Visible' : 'Hidden',
      visibilityColor: product.product_visible ? 'green' : 'red',
      hasImage: !!product.product_image,
      shortDescription: product.product_description && product.product_description.length > 100 
        ? product.product_description.substring(0, 100) + '...'
        : product.product_description,
    };
  }, []);

  // Get product summary for a business
  const getProductSummary = useCallback(async (registryNumber) => {
    validateAccess();

    try {
      const [stats, products] = await Promise.all([
        getProductStats(registryNumber),
        getProductsByRegistryNumber(registryNumber, { limit: 10 }) // Get sample products
      ]);

      return {
        registryNumber,
        totalProducts: stats.stats.total_products,
        visibleProducts: stats.stats.visible_products,
        hiddenProducts: stats.stats.hidden_products,
        inStockProducts: stats.stats.in_stock_products,
        outOfStockProducts: stats.stats.out_of_stock_products,
        averagePrice: stats.stats.average_price,
        sampleProducts: products.products,
        accessLevel: loginType,
        hasProducts: stats.stats.total_products > 0,
        visibilityRate: stats.stats.total_products > 0 
          ? (stats.stats.visible_products / stats.stats.total_products * 100).toFixed(1) + '%'
          : '0%',
        stockRate: stats.stats.total_products > 0 
          ? (stats.stats.in_stock_products / stats.stats.total_products * 100).toFixed(1) + '%'
          : '0%',
      };
    } catch (err) {
      const errorMessage = err.message || 'Failed to get product summary';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [getProductStats, getProductsByRegistryNumber, validateAccess, loginType]);

  // Get product price statistics
  const getProductPriceStats = useCallback(async (registryNumber) => {
    validateAccess();

    try {
      const result = await getProductsByRegistryNumber(registryNumber, { limit: 1000 });
      const prices = result.products.map(p => p.product_price);
      
      if (prices.length === 0) {
        return {
          registryNumber,
          count: 0,
          averagePrice: 0,
          minPrice: 0,
          maxPrice: 0,
          medianPrice: 0,
        };
      }

      const sortedPrices = prices.sort((a, b) => a - b);
      const sum = prices.reduce((total, price) => total + price, 0);
      const average = sum / prices.length;
      const median = prices.length % 2 === 0
        ? (sortedPrices[prices.length / 2 - 1] + sortedPrices[prices.length / 2]) / 2
        : sortedPrices[Math.floor(prices.length / 2)];

      return {
        registryNumber,
        count: prices.length,
        averagePrice: average,
        minPrice: Math.min(...prices),
        maxPrice: Math.max(...prices),
        medianPrice: median,
        priceDistribution: {
          under10: prices.filter(p => p < 10).length,
          from10to50: prices.filter(p => p >= 10 && p < 50).length,
          from50to100: prices.filter(p => p >= 50 && p < 100).length,
          over100: prices.filter(p => p >= 100).length,
        }
      };
    } catch (err) {
      const errorMessage = err.message || 'Failed to get price statistics';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [getProductsByRegistryNumber, validateAccess]);

  // Get current endpoint info for debugging
  const getEndpointInfo = useCallback(() => {
    return {
      loginType,
      baseUrl: loginType === 'master' 
        ? `${API_BASE_URL}/master/business-products/api/v1/business-products`
        : `${API_BASE_URL}/admin/business-products/api/v1/business-products`,
      accessLevel: loginType === 'master' ? 'Master Access' : 'Admin Access',
      requiredHeaders: loginType === 'master' 
        ? ['Authorization', 'secret_credential']
        : ['Authorization', 'admin_id'],
    };
  }, [loginType, API_BASE_URL]);

  const value = {
    // State
    loading,
    error,
    
    // Core Actions
    getProductsByRegistryNumber,
    getProductStats,
    getProductStatuses,
    getProductById,
    getProductByProductId,
    
    // Search and Filter Actions
    getProductsByStatus,
    getProductsByVisibility,
    getProductsByPriceRange,
    searchProducts,
    getVisibleProducts,
    getInStockProducts,
    getOutOfStockProducts,
    
    // Utility Actions
    exportProductsData,
    checkHealth,
    getProductSummary,
    getProductPriceStats,
    
    // Helpers
    clearError,
    validateProductFilters,
    formatProductData,
    getEndpointInfo,
    
    // Computed values
    isAdminUser: loginType === 'admin',
    isMasterUser: loginType === 'master',
    hasProductsAccess: loginType === 'admin' || loginType === 'master',
    currentAccessLevel: loginType,
  };

  return (
    <BusinessProductContext.Provider value={value}>
      {children}
    </BusinessProductContext.Provider>
  );
};